
import { useState } from 'react';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CalendarDateRangePicker } from '@/components/analytics/DateRangePicker';
import { MetricCard } from '@/components/analytics/MetricCard';
import { Separator } from '@/components/ui/separator';
import { TeamPerformanceTable } from '@/components/analytics/TeamPerformanceTable';
import { Button } from '@/components/ui/button';
import { Download, BarChart3, LineChart, PieChart, Maximize2, Filter } from 'lucide-react';
import { ResponsiveContainer, PieChart as RePieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart as ReLineChart, Line } from 'recharts';

// Sample data
const teamPerformanceData = [
  {
    name: "Frontend Team",
    velocity: 84,
    completionRate: 92,
    qualityScore: 88,
    tasks: 56
  },
  {
    name: "Backend Team",
    velocity: 78,
    completionRate: 85,
    qualityScore: 92,
    tasks: 42
  },
  {
    name: "Design Team",
    velocity: 92,
    completionRate: 96,
    qualityScore: 95,
    tasks: 38
  },
  {
    name: "QA Team",
    velocity: 76,
    completionRate: 88,
    qualityScore: 90,
    tasks: 64
  },
  {
    name: "DevOps Team",
    velocity: 82,
    completionRate: 90,
    qualityScore: 87,
    tasks: 29
  },
];

const skillDistributionData = [
  { name: 'React', value: 38 },
  { name: 'TypeScript', value: 28 },
  { name: 'Node.js', value: 16 },
  { name: 'UX Design', value: 12 },
  { name: 'DevOps', value: 6 },
];

const monthlyDeliveryData = [
  { name: 'Jan', planned: 30, actual: 28 },
  { name: 'Feb', planned: 35, actual: 32 },
  { name: 'Mar', planned: 40, actual: 42 },
  { name: 'Apr', planned: 45, actual: 40 },
  { name: 'May', planned: 50, actual: 53 },
  { name: 'Jun', planned: 55, actual: 58 },
];

const resourceUtilizationData = [
  { name: 'Week 1', frontend: 75, backend: 68, design: 82, qa: 62 },
  { name: 'Week 2', frontend: 82, backend: 73, design: 88, qa: 70 },
  { name: 'Week 3', frontend: 88, backend: 78, design: 79, qa: 76 },
  { name: 'Week 4', frontend: 85, backend: 82, design: 76, qa: 85 },
];

export default function Analytics() {
  const [activeTab, setActiveTab] = useState('performance');

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-2 md:space-y-0">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Analytics</h2>
            <p className="text-muted-foreground">
              Track project metrics, team performance, and resource utilization
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <CalendarDateRangePicker />
            <Button variant="outline" size="sm">
              <Filter className="mr-2 h-4 w-4" />
              Filter
            </Button>
            <Button variant="outline" size="sm">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </div>
        </div>
        <Separator />

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Sprint Velocity"
            value="42 SP"
            description="Average story points per sprint"
            trend={8}
            trendLabel="vs last sprint"
            icon={BarChart3}
          />
          <MetricCard
            title="Completion Rate"
            value="92%"
            description="Sprint commitment completion"
            trend={4}
            trendLabel="vs last sprint"
            icon={PieChart}
          />
          <MetricCard
            title="Cycle Time"
            value="3.8 days"
            description="Average time to complete a task"
            trend={-0.6}
            trendLabel="vs last sprint"
            icon={LineChart}
          />
          <MetricCard
            title="Quality Score"
            value="86/100"
            description="Based on code quality and defects"
            trend={2}
            trendLabel="vs last sprint"
            icon={BarChart3}
          />
        </div>

        <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
            <TabsTrigger value="performance">Team Performance</TabsTrigger>
            <TabsTrigger value="resources">Resource Utilization</TabsTrigger>
            <TabsTrigger value="skills">Skills Analysis</TabsTrigger>
            <TabsTrigger value="delivery">Delivery Metrics</TabsTrigger>
          </TabsList>
          
          <TabsContent value="performance" className="mt-4 space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div>
                  <CardTitle>Team Performance Overview</CardTitle>
                  <CardDescription>
                    Compare velocity, completion rate, and quality scores across teams
                  </CardDescription>
                </div>
                <Button variant="outline" size="icon">
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent>
                <TeamPerformanceTable data={teamPerformanceData} />
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div>
                  <CardTitle>Performance Metrics by Team</CardTitle>
                  <CardDescription>
                    Visualization of key performance indicators
                  </CardDescription>
                </div>
                <Button variant="outline" size="icon">
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={teamPerformanceData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="velocity" fill="#8884d8" name="Velocity" />
                    <Bar dataKey="completionRate" fill="#82ca9d" name="Completion Rate" />
                    <Bar dataKey="qualityScore" fill="#ffc658" name="Quality Score" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="resources" className="mt-4 space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div>
                  <CardTitle>Resource Utilization</CardTitle>
                  <CardDescription>
                    Team resource allocation and utilization over time
                  </CardDescription>
                </div>
                <Button variant="outline" size="icon">
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <ReLineChart
                    data={resourceUtilizationData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`${value}%`, 'Utilization']} />
                    <Legend />
                    <Line type="monotone" dataKey="frontend" stroke="#8884d8" name="Frontend" />
                    <Line type="monotone" dataKey="backend" stroke="#82ca9d" name="Backend" />
                    <Line type="monotone" dataKey="design" stroke="#ffc658" name="Design" />
                    <Line type="monotone" dataKey="qa" stroke="#ff8042" name="QA" />
                  </ReLineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="skills" className="mt-4 space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div>
                  <CardTitle>Team Skills Distribution</CardTitle>
                  <CardDescription>
                    Breakdown of skills across the organization
                  </CardDescription>
                </div>
                <Button variant="outline" size="icon">
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="flex justify-center">
                <ResponsiveContainer width="100%" height={350}>
                  <RePieChart>
                    <Pie
                      data={skillDistributionData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={130}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {skillDistributionData.map((entry, index) => (
                        <Tooltip key={`cell-${index}`} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value}%`, name]} />
                  </RePieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="delivery" className="mt-4 space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div>
                  <CardTitle>Delivery Performance</CardTitle>
                  <CardDescription>
                    Comparison of planned vs. actual delivery metrics
                  </CardDescription>
                </div>
                <Button variant="outline" size="icon">
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={monthlyDeliveryData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="planned" fill="#8884d8" name="Planned Deliveries" />
                    <Bar dataKey="actual" fill="#82ca9d" name="Actual Deliveries" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
